import dayjs from "dayjs";

const normalizeName = (value) => (value || "").toString().trim().toLowerCase();

export const getLabTestRowId = (labTest) =>
  // Prefer backend ids first; UI-merged labTestId can be stale.
  labTest?.lab_test_id ??
  labTest?.test_id ??
  labTest?.testId ??
  labTest?.labTestId ??
  null;

/** Normalize API snake_case fields onto canonical report row shape. */
export const normalizePathLabReportFields = (row) => {
  if (!row) return row;

  const rawCatalogId =
    row?.lab_test_id ??
    row?.test_id ??
    row?.testId ??
    row?.labTestId ??
    null;
  const parsedCatalogId = Number(rawCatalogId);
  const catalogId = Number.isNaN(parsedCatalogId) ? null : parsedCatalogId;
  const reportId = getPathLabReportId(row);
  const testName =
    row?.testName ||
    row?.test_name ||
    row?.name ||
    row?.labTestName ||
    row?.lab_test_name ||
    "";

  return {
    ...row,
    reportId: reportId ?? row?.report_id ?? row?.id ?? null,
    id: reportId ?? row?.id ?? row?.report_id ?? null,
    testId: catalogId ?? row?.testId ?? row?.labTestId ?? row?.lab_test_id ?? null,
    labTestId: catalogId ?? row?.labTestId ?? row?.testId ?? row?.lab_test_id ?? null,
    testName,
    result: getPathLabReportResultValue(row),
    resultStatus: row?.resultStatus ?? row?.result_status ?? row?.status ?? null,
    reportDate:
      row?.reportDate ??
      row?.report_date ??
      row?.createdAt ??
      row?.created_at ??
      row?.date ??
      null,
  };
};

export const getLabTestCatalogId = (row) => {
  const id = getLabTestRowId(row);
  if (id == null || id === "") return null;
  const num = Number(id);
  return Number.isNaN(num) ? null : num;
};

/**
 * Resolve path-lab report primary key from a path-lab report API/table row.
 * Prefer reportId/report_id; fall back to id for real report payloads.
 *
 * Do NOT call this on prescription patientLabTests to invent a report id —
 * use getBoundPathLabReportId() there (explicit reportId only).
 */
export const getPathLabReportId = (row) => {
  if (!row) return null;

  const explicit = row?.reportId ?? row?.report_id ?? null;
  if (explicit != null && explicit !== "") {
    const num = Number(explicit);
    if (!Number.isNaN(num)) return num;
  }

  // Nested reportData means this object is a lab-test wrapper, not a report row.
  if (row?.reportData != null) return null;
  // IPD patient-lab-test rows use a different id space.
  if (row?.admissionId != null || row?.testStatus != null) return null;
  if (row?.__entity === "patientLabTest" || row?._isPatientLabTest === true) {
    return null;
  }

  if (row?.id != null && row?.id !== "") {
    const looksLikeReportRow =
      row?.resultStatus != null ||
      row?.result_status != null ||
      row?.reportDate != null ||
      row?.report_date != null ||
      (row?.patientId != null && (row?.testName != null || row?.test_name != null));
    if (looksLikeReportRow) {
      const num = Number(row.id);
      if (!Number.isNaN(num)) return num;
    }
  }

  return null;
};

/**
 * Report id stamped onto a prescription patientLabTest.
 * Only trusts explicit reportId/report_id — never the pathology/lab-test PK.
 */
export const getBoundPathLabReportId = (labTest) => {
  if (!labTest) return null;
  const explicit = labTest?.reportId ?? labTest?.report_id ?? null;
  if (explicit == null || explicit === "") return null;
  const num = Number(explicit);
  return Number.isNaN(num) ? null : num;
};

export const buildPrescriptionLabTestIndex = (patientLabTests = []) => {
  const testNames = new Set();
  const labTestIds = new Set();

  (Array.isArray(patientLabTests) ? patientLabTests : []).forEach((test) => {
    const name = normalizeName(
      test?.testName || test?.test_name || test?.name || test?.labTestName
    );
    if (name) testNames.add(name);

    const labTestId = getLabTestRowId(test);
    if (labTestId != null && labTestId !== "") {
      labTestIds.add(Number(labTestId));
    }
  });

  return { testNames, labTestIds };
};

export const isLabTestAlreadyOnPrescription = (index, reportOrTest) => {
  const idx = index || { testNames: new Set(), labTestIds: new Set() };
  const labTestId = getLabTestRowId(reportOrTest);
  if (labTestId != null && labTestId !== "" && idx.labTestIds.has(Number(labTestId))) {
    return true;
  }

  const testName = normalizeName(
    reportOrTest?.testName ||
      reportOrTest?.test_name ||
      reportOrTest?.name ||
      reportOrTest?.labTestName
  );
  return Boolean(testName && idx.testNames.has(testName));
};

export const registerLabTestOnPrescriptionIndex = (index, labTest) => {
  const name = normalizeName(
    labTest?.testName || labTest?.test_name || labTest?.name || labTest?.labTestName
  );
  if (name) index.testNames.add(name);

  const labTestId = getLabTestRowId(labTest);
  if (labTestId != null && labTestId !== "") {
    index.labTestIds.add(Number(labTestId));
  }
};

/** Deduplicate lab tests within a single prescription (by labTestId, then test name). */
export const dedupePrescriptionLabTests = (patientLabTests = []) => {
  const result = [];
  const seenIds = new Set();
  const seenNames = new Set();

  (Array.isArray(patientLabTests) ? patientLabTests : []).forEach((test) => {
    const labTestId = getLabTestRowId(test);
    const name = normalizeName(
      test?.testName || test?.test_name || test?.name || test?.labTestName
    );

    if (labTestId != null && labTestId !== "") {
      const idKey = Number(labTestId);
      if (seenIds.has(idKey)) return;
      seenIds.add(idKey);
    } else if (name) {
      if (seenNames.has(name)) return;
      seenNames.add(name);
    } else {
      return;
    }

    result.push(test);
  });

  return result;
};

const getPathLabReportDay = (row) => {
  const dateKey = resolvePathLabReportDateKey(row);
  return dateKey || "";
};

const toReportDateValue = (value) => {
  if (value == null || value === "") return null;
  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    if (!year || !month || !day) return null;
    const parsed = dayjs(new Date(year, month - 1, day, hour, minute, second));
    return parsed.isValid() ? parsed : null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

/** YYYY-MM-DD grouping key for path-lab report rows (supports ISO strings and date arrays). */
export const resolvePathLabReportDateKey = (row) => {
  const candidates = [
    row?.reportDate,
    row?.createdAt,
    row?.created_at,
    row?.updatedAt,
    row?.updated_at,
    row?.testDate,
  ];

  for (const candidate of candidates) {
    const parsed = toReportDateValue(candidate);
    if (parsed?.isValid()) return parsed.format("YYYY-MM-DD");
  }

  return null;
};

export const resolvePathLabReportDate = (row) => {
  const dateKey = resolvePathLabReportDateKey(row);
  return dateKey ? `${dateKey}T00:00:00` : null;
};

export const getPathLabReportResultValue = (row) =>
  row?.result ?? row?.testResult ?? row?.test_result ?? null;

export const hasPathLabReportResult = (row) => {
  const value = getPathLabReportResultValue(row);
  if (value == null) return false;
  const text = String(value).trim();
  return text !== "" && text.toLowerCase() !== "null";
};

const mergePathLabReportRecords = (base, overlay) => {
  const baseTestId = getLabTestCatalogId(base);
  const overlayTestId = getLabTestCatalogId(overlay);
  const baseReportId = getPathLabReportId(base);
  const overlayReportId = getPathLabReportId(overlay);

  if (
    baseReportId != null &&
    overlayReportId != null &&
    baseReportId !== overlayReportId
  ) {
    return overlayReportId >= baseReportId ? overlay : base;
  }

  if (
    baseTestId != null &&
    overlayTestId != null &&
    baseTestId !== overlayTestId
  ) {
    if (baseReportId != null && overlayReportId != null && baseReportId === overlayReportId) {
      const baseName = normalizeName(
        base?.testName || base?.test_name || base?.name || base?.labTestName
      );
      const overlayName = normalizeName(
        overlay?.testName || overlay?.test_name || overlay?.name || overlay?.labTestName
      );
      if (baseName && overlayName && baseName !== overlayName) {
        return hasPathLabReportResult(overlay) && !hasPathLabReportResult(base) ? overlay : base;
      }
    }
    return overlayReportId >= baseReportId ? overlay : base;
  }

  const baseName = normalizeName(
    base?.testName || base?.test_name || base?.name || base?.labTestName
  );
  const overlayName = normalizeName(
    overlay?.testName || overlay?.test_name || overlay?.name || overlay?.labTestName
  );
  if (baseName && overlayName && baseName !== overlayName) {
    return hasPathLabReportResult(overlay) ? overlay : base;
  }

  const merged = { ...base, ...overlay };

  if (hasPathLabReportResult(base) && !hasPathLabReportResult(overlay)) {
    merged.result = getPathLabReportResultValue(base);
  } else if (hasPathLabReportResult(overlay)) {
    merged.result = getPathLabReportResultValue(overlay);
  }

  if (!merged.normalRange && (base?.normalRange || overlay?.normalRange)) {
    merged.normalRange = overlay?.normalRange ?? base?.normalRange;
  }

  if (!resolvePathLabReportDateKey(merged)) {
    const fallbackDate = resolvePathLabReportDate(overlay) || resolvePathLabReportDate(base);
    if (fallbackDate) merged.reportDate = fallbackDate;
  }

  if (!merged.testId && !merged.labTestId && (base?.testId || overlay?.testId)) {
    merged.testId = overlay?.testId ?? overlay?.labTestId ?? base?.testId ?? base?.labTestId;
    merged.labTestId = merged.testId;
  }

  if (!merged.testName && (base?.testName || overlay?.testName)) {
    merged.testName = overlay?.testName ?? base?.testName;
  }

  return merged;
};

const preferPathLabReportRecord = (current, candidate) => {
  const currentReportId = Number(current?.reportId ?? current?.id ?? 0);
  const candidateReportId = Number(candidate?.reportId ?? candidate?.id ?? 0);
  if (
    currentReportId > 0 &&
    candidateReportId > 0 &&
    currentReportId !== candidateReportId
  ) {
    return candidateReportId >= currentReportId ? candidate : current;
  }
  if (currentReportId > 0 && currentReportId === candidateReportId) {
    return mergePathLabReportRecords(current, candidate);
  }

  const statusRank = (row) => {
    const status = String(row?.resultStatus || row?.status || "").toUpperCase();
    const hasResult = hasPathLabReportResult(row);
    if ((status === "COMPLETE" || status === "COMPLETED") && hasResult) return 4;
    if (status === "COMPLETE" || status === "COMPLETED") return 3;
    if (status === "BILLED" || status === "COLLECTED") return 2;
    if (hasResult) return 1;
    return 0;
  };

  const currentRank = statusRank(current);
  const candidateRank = statusRank(candidate);
  if (candidateRank !== currentRank) {
    const preferred = candidateRank > currentRank ? candidate : current;
    const other = preferred === candidate ? current : candidate;
    return mergePathLabReportRecords(other, preferred);
  }

  const currentHasResult = hasPathLabReportResult(current);
  const candidateHasResult = hasPathLabReportResult(candidate);
  if (currentHasResult !== candidateHasResult) {
    const preferred = candidateHasResult ? candidate : current;
    const other = preferred === candidate ? current : candidate;
    return mergePathLabReportRecords(other, preferred);
  }

  const currentId = Number(current?.reportId ?? current?.id ?? 0);
  const candidateId = Number(candidate?.reportId ?? candidate?.id ?? 0);
  const preferred = candidateId >= currentId ? candidate : current;
  const other = preferred === candidate ? current : candidate;
  return mergePathLabReportRecords(other, preferred);
};

const buildPathLabReportDedupeKey = (row) => {
  const testId = getLabTestRowId(row) ?? row?.testId ?? null;
  const day = getPathLabReportDay(row);
  const testName = normalizeName(
    row?.testName || row?.test_name || row?.name || row?.labTestName
  );
  const prescriptionId = row?.prescriptionId ?? row?.prescription_id ?? null;
  const patientId = row?.patientId ?? row?.patient_id ?? null;
  const reportId = getPathLabReportId(row);

  if (reportId != null) return `report:${reportId}`;
  if (testId != null && testId !== "" && day && patientId != null) {
    if (testName) return `td:${patientId}:${testId}:${testName}:${day}`;
    return `td:${patientId}:${testId}:${day}`;
  }
  if (testName && day && patientId != null) return `tn:${patientId}:${testName}:${day}`;
  if (testId != null && prescriptionId != null) return `tp:${testId}:${prescriptionId}`;
  if (row?.id != null) return `row:${row.id}`;
  return null;
};

/** Deduplicate path-lab report records (same test on same day → keep best row). */
export const dedupePathLabReportRecords = (records = []) => {
  const byKey = new Map();

  (Array.isArray(records) ? records : []).forEach((row) => {
    const key = buildPathLabReportDedupeKey(row);
    if (!key) return;

    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, row);
      return;
    }

    byKey.set(key, preferPathLabReportRecord(existing, row));
  });

  return Array.from(byKey.values());
};

/** Deduplicate path-lab report rows for patient dashboard lists. */
export const dedupePatientLabReportRows = (rows = []) =>
  dedupePathLabReportRecords(rows);

const prescriptionDisplaySignature = (prescription) => {
  const tests = dedupePrescriptionLabTests(prescription?.patientLabTests || []);
  const testIds = tests
    .map((t) => getLabTestRowId(t))
    .filter((id) => id != null && id !== "")
    .map(Number)
    .sort((a, b) => a - b)
    .join(",");
  const testNames = tests
    .map((t) =>
      normalizeName(t?.testName || t?.test_name || t?.name || t?.labTestName)
    )
    .filter(Boolean)
    .sort()
    .join(",");
  const day = prescription?.createdAt
    ? dayjs(prescription.createdAt).format("YYYY-MM-DD")
    : "";

  return `${prescription?.patientId}|${prescription?.doctorId}|${day}|${
    testIds || testNames
  }`;
};

const preferPrescriptionRow = (current, candidate) => {
  const currentVirtual = String(current?.id || "").startsWith("virtual_");
  const candidateVirtual = String(candidate?.id || "").startsWith("virtual_");
  if (currentVirtual !== candidateVirtual) {
    return candidateVirtual ? current : candidate;
  }

  const currentId = Number(current?.id);
  const candidateId = Number(candidate?.id);
  if (!Number.isNaN(currentId) && !Number.isNaN(candidateId)) {
    return candidateId > currentId ? candidate : current;
  }

  const currentTests = (current?.patientLabTests || []).length;
  const candidateTests = (candidate?.patientLabTests || []).length;
  return candidateTests > currentTests ? candidate : current;
};

/** Collapse duplicate OPD queue cards (same patient/doctor/day/tests). */
export const dedupeOpdPrescriptionsForDisplay = (prescriptions = []) => {
  const withDedupedTests = (Array.isArray(prescriptions) ? prescriptions : []).map(
    (prescription) => ({
      ...prescription,
      patientLabTests: dedupePrescriptionLabTests(prescription?.patientLabTests),
    })
  );

  const bySignature = new Map();
  withDedupedTests.forEach((prescription) => {
    const signature = prescriptionDisplaySignature(prescription);
    if (!signature.endsWith("|") && signature.split("|")[3]) {
      const existing = bySignature.get(signature);
      bySignature.set(
        signature,
        existing ? preferPrescriptionRow(existing, prescription) : prescription
      );
    } else {
      bySignature.set(`${signature}:${prescription?.id}`, prescription);
    }
  });

  return Array.from(bySignature.values());
};
